/**
 * Month-calendar browse for a brand's catalog.
 *
 * The list view answers "what does this brand offer"; this answers "what is on that Saturday",
 * which is the question a family with a weekend actually has. Both read the same rows — this
 * is a second view of the catalog, never a second source of truth.
 *
 * Layout is one grid with a day panel beneath it, at every width. On a phone a 7-column grid
 * gives each day about 50px, which cannot hold a readable program name, so narrow screens get
 * colour dots in the cell and the names in the panel; from `sm` up the cell carries named
 * chips as well. Nothing is hidden on a phone that a wider screen shows — it moves.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    monthBounds,
    monthGridDates,
    sessionDatesBetween,
    type SessionDateProgram,
} from '../utils/programSessionDates';
import { getProgramTypeColor, getProgramTypeDisplayName, isEnrollmentLocked } from '../types/program';
import { formatSessionTimeRange } from '../utils/schedule';
import { getTodayDateStringInBusinessTZ } from '../utils/date';
import { brandProfileOf } from '../config/brandProfiles';
import type { BrandKey } from '../config/brands';
import { programLocationName, type WithProgramLocation } from '../types/location';
import { LocationPill } from './LocationPill';

export interface CalendarProgram extends SessionDateProgram, WithProgramLocation {
    id: string;
    name: string;
    price: number;
    billing_type: string;
    duration?: number | null;
    enrollment_status?: string | null;
    /** Capped and no seat left (BHCS-124); its page offers the waitlist. */
    full?: boolean;
    /** The price with the program's eligibility discount (e.g. Foster City Resident), if any. */
    discounted?: { holderLabel: string; price: number } | null;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** `YYYY-MM-DD` → the pieces a header needs, without going through `Date` parsing. */
function partsOf(ymd: string): { year: number; month: number; day: number } {
    const [year, month, day] = ymd.split('-').map(Number);
    return { year, month: month - 1, day };
}

function monthLabel(year: number, month: number): string {
    return new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function dayLabel(ymd: string): string {
    const { year, month, day } = partsOf(ymd);
    return new Date(year, month, day).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
}

function priceLabel(program: CalendarProgram, price: number = program.price): string {
    if (!price || price <= 0) return 'Free';
    const amount = Number(price).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: Number.isInteger(Number(price)) ? 0 : 2,
    });
    if (program.billing_type === 'recurring_monthly') return `${amount}/mo`;
    if (program.billing_type === 'per_day' || program.billing_type === 'flex_monthly') return `${amount}/day`;
    return amount;
}

export function ProgramCalendarView({
    programs,
    brand,
}: {
    programs: CalendarProgram[];
    brand: BrandKey;
}) {
    const navigate = useNavigate();
    const profile = brandProfileOf(brand);
    const locationOf = (program: CalendarProgram) => programLocationName(program);
    const today = getTodayDateStringInBusinessTZ();

    const [cursor, setCursor] = useState(() => {
        const { year, month } = partsOf(today);
        return { year, month };
    });
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const { first, last } = monthBounds(cursor.year, cursor.month);
    const gridDates = useMemo(() => monthGridDates(cursor.year, cursor.month), [cursor]);

    // The grid spills into the neighbouring months, so sessions are expanded across the whole
    // visible window — otherwise the first days of next month render empty while showing.
    const windowStart = gridDates[0];
    const windowEnd = gridDates[gridDates.length - 1];

    const byDate = useMemo(() => {
        const map = new Map<string, CalendarProgram[]>();
        for (const program of programs) {
            for (const ymd of sessionDatesBetween(program, windowStart, windowEnd, brand)) {
                const bucket = map.get(ymd);
                if (bucket) bucket.push(program);
                else map.set(ymd, [program]);
            }
        }
        for (const bucket of map.values()) {
            bucket.sort((a, b) => a.name.localeCompare(b.name));
        }
        return map;
    }, [programs, windowStart, windowEnd, brand]);

    // Falls back to today, then to the month's first day with anything on it, so the panel is
    // never empty on a month the parent has just paged into.
    const activeDate = useMemo(() => {
        if (selectedDate && selectedDate >= windowStart && selectedDate <= windowEnd) return selectedDate;
        if (today >= first && today <= last) return today;
        const firstWithSessions = gridDates.find(d => d >= first && d <= last && byDate.has(d));
        return firstWithSessions ?? first;
    }, [selectedDate, windowStart, windowEnd, today, first, last, gridDates, byDate]);

    const activePrograms = byDate.get(activeDate) ?? [];

    const stepMonth = (delta: number) => {
        setSelectedDate(null);
        setCursor(({ year, month }) => {
            const next = new Date(year, month + delta, 1);
            return { year: next.getFullYear(), month: next.getMonth() };
        });
    };

    const goToday = () => {
        const { year, month } = partsOf(today);
        setCursor({ year, month });
        setSelectedDate(today);
    };

    return (
        <div className="px-3 sm:px-0">
            {/* Month header */}
            <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    {monthLabel(cursor.year, cursor.month)}
                </h2>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={goToday}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                        Today
                    </button>
                    <button
                        type="button"
                        onClick={() => stepMonth(-1)}
                        aria-label="Previous month"
                        className="p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => stepMonth(1)}
                        aria-label="Next month"
                        className="p-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-px mb-px">
                {WEEKDAY_LABELS.map(label => (
                    <div
                        key={label}
                        className="py-1.5 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-400"
                    >
                        <span className="sm:hidden">{label[0]}</span>
                        <span className="hidden sm:inline">{label}</span>
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden border border-gray-200">
                {gridDates.map(ymd => {
                    const { day, month } = partsOf(ymd);
                    const inMonth = month === cursor.month;
                    const dayPrograms = byDate.get(ymd) ?? [];
                    const isToday = ymd === today;
                    const isActive = ymd === activeDate;
                    return (
                        <button
                            type="button"
                            key={ymd}
                            onClick={() => setSelectedDate(ymd)}
                            aria-label={`${dayLabel(ymd)}, ${dayPrograms.length} program${dayPrograms.length === 1 ? '' : 's'}`}
                            aria-pressed={isActive}
                            className={`min-h-[62px] sm:min-h-[96px] p-1 sm:p-1.5 text-left align-top transition-colors ${
                                inMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            style={isActive ? { backgroundColor: profile.accentSoft } : undefined}
                        >
                            <span
                                className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] sm:text-xs font-semibold ${
                                    inMonth ? 'text-gray-700' : 'text-gray-400'
                                }`}
                                style={
                                    isToday
                                        ? { backgroundColor: profile.accent, color: '#fff' }
                                        : undefined
                                }
                            >
                                {day}
                            </span>

                            {/* Phone: one dot per program. */}
                            <span className="mt-1 flex flex-wrap gap-0.5 sm:hidden">
                                {dayPrograms.slice(0, 4).map(program => (
                                    <span
                                        key={program.id}
                                        className="h-1.5 w-1.5 rounded-full"
                                        style={{ backgroundColor: getProgramTypeColor(program.type) }}
                                    />
                                ))}
                                {dayPrograms.length > 4 && (
                                    <span className="text-[9px] leading-none text-gray-400">
                                        +{dayPrograms.length - 4}
                                    </span>
                                )}
                            </span>

                            {/* sm and up: named chips. */}
                            <span className="mt-1 hidden sm:flex flex-col gap-0.5">
                                {dayPrograms.slice(0, 3).map(program => {
                                    const location = locationOf(program);
                                    return (
                                        <span
                                            key={program.id}
                                            className="flex min-w-0 flex-col rounded px-1 py-0.5 text-[10px] font-medium text-white"
                                            style={{ backgroundColor: getProgramTypeColor(program.type) }}
                                            title={location ? `${program.name} · ${location}` : program.name}
                                        >
                                            <span className="truncate">{program.name}</span>
                                            {location && (
                                                <span className="truncate text-[9px] font-normal opacity-90">
                                                    {location}
                                                </span>
                                            )}
                                        </span>
                                    );
                                })}
                                {dayPrograms.length > 3 && (
                                    <span className="px-1 text-[10px] font-medium text-gray-500">
                                        +{dayPrograms.length - 3} more
                                    </span>
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Selected-day panel */}
            <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{dayLabel(activeDate)}</h3>
                {activePrograms.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                        Nothing scheduled. Pick another day to see what's on.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {activePrograms.map(program => {
                            const time = formatSessionTimeRange(program.schedule_cron, program.duration ?? null);
                            const locked = isEnrollmentLocked(program);
                            return (
                                <li key={program.id}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/programs/${program.id}`)}
                                        className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-gray-300 hover:shadow-sm transition-all"
                                    >
                                        <div className="flex items-start gap-3">
                                            <span
                                                className="mt-1 h-8 w-1.5 flex-shrink-0 rounded-full"
                                                style={{ backgroundColor: getProgramTypeColor(program.type) }}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-gray-900">
                                                    {program.name}
                                                </p>
                                                <p className="mt-0.5 text-xs text-gray-500">
                                                    {getProgramTypeDisplayName(program.type)}
                                                    {time ? ` · ${time}` : ''}
                                                </p>
                                                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                                        {priceLabel(program)}
                                                    </span>
                                                    {program.discounted && (
                                                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                                            {program.discounted.holderLabel} {priceLabel(program, program.discounted.price)}
                                                        </span>
                                                    )}
                                                    <LocationPill name={locationOf(program)} />
                                                    {locked && (
                                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                                            Enrollment closed
                                                        </span>
                                                    )}
                                                    {program.full && !locked && (
                                                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                                            Full
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
